import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import adminApi from '../api/admin.api';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useToast } from '../contexts/ToastContext';
import { AuditLog, CampaignStatus, RevenueReport, SystemSettings, User, UserRole } from '../types/campaign.types';
import { formatDate, formatNumber, formatRelativeTime } from '../utils/formatters';

type AdminSection = 'revenue' | 'pricing' | 'staff' | 'audit';

type ExportField = {
    key: string;
    label: string;
};

const exportFields: ExportField[] = [
    { key: 'campaign_name', label: 'Campaign' },
    { key: 'status', label: 'Status' },
    { key: 'total_contacts', label: 'Contacts' },
    { key: 'total_sent', label: 'Sent' },
    { key: 'total_delivered', label: 'Delivery receipts' },
    { key: 'total_failed', label: 'Failed' },
    { key: 'estimated_revenue', label: 'Estimated revenue' },
    { key: 'actual_revenue', label: 'Actual revenue' },
    { key: 'currency', label: 'Currency' },
    { key: 'created_at', label: 'Created date' },
    { key: 'completed_at', label: 'Completed date' },
];

const exportStatuses = [
    CampaignStatus.PROCESSING,
    CampaignStatus.IN_PROGRESS,
    CampaignStatus.COMPLETED,
    CampaignStatus.FAILED,
    CampaignStatus.CANCELLED,
];

const roleOptions: UserRole[] = ['staff', 'viewer', 'admin'];

const money = (value: number, currency = 'GHS') => `${currency} ${formatNumber(Number(value || 0))}`;

const prettyAction = (action?: string) => action
    ? action.replace(/[_-]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : 'No Activity';

const statusClass = (status: string) => clsx(
    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize',
    status === 'completed' && 'bg-green-50 text-green-700 ring-1 ring-green-200',
    status === 'processing' && 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
    status === 'failed' && 'bg-red-50 text-red-700 ring-1 ring-red-200',
    !['completed', 'processing', 'failed'].includes(status) && 'bg-gray-50 text-gray-700 ring-1 ring-gray-200'
);

const statusCodeClass = (statusCode?: number) => clsx(
    'inline-flex min-w-12 justify-center rounded-full px-2 py-1 text-xs font-semibold',
    statusCode && statusCode >= 200 && statusCode < 300 && 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    statusCode && statusCode >= 400 && 'bg-red-50 text-red-700 ring-1 ring-red-200',
    !statusCode && 'bg-gray-100 text-gray-600'
);

const StatCard: React.FC<{
    label: string;
    value: string;
    detail?: string;
    helper?: string;
    tone?: 'neutral' | 'green' | 'blue';
}> = ({
    label,
    value,
    detail,
    helper,
    tone = 'neutral',
}) => (
    <div className="min-h-32 rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
            {helper && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{helper}</span>}
        </div>
        <p className={clsx(
            'mt-3 truncate text-2xl font-bold tracking-tight',
            tone === 'green' && 'text-green-700',
            tone === 'blue' && 'text-blue-700',
            tone === 'neutral' && 'text-gray-950'
        )}>
            {value}
        </p>
        {detail && <p className="mt-2 truncate text-sm text-gray-500">{detail}</p>}
    </div>
);

const EmptyState: React.FC<{ title: string; detail: string; colSpan: number }> = ({ title, detail, colSpan }) => (
    <tr>
        <td colSpan={colSpan} className="px-5 py-12 text-center">
            <p className="font-semibold text-gray-900">{title}</p>
            <p className="mt-1 text-sm text-gray-500">{detail}</p>
        </td>
    </tr>
);

const AdminPage: React.FC = () => {
    const { showToast } = useToast();
    const [settings, setSettings] = useState<SystemSettings | null>(null);
    const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
    const [auditUserFilter, setAuditUserFilter] = useState('');
    const [auditStartDate, setAuditStartDate] = useState('');
    const [auditEndDate, setAuditEndDate] = useState('');
    const [users, setUsers] = useState<User[]>([]);
    const [newUser, setNewUser] = useState({
        email: '',
        full_name: '',
        password: '',
        role: 'staff' as UserRole,
    });
    const [showCreateUserForm, setShowCreateUserForm] = useState(false);
    const [creatingUser, setCreatingUser] = useState(false);
    const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
    const [pricingPlans, setPricingPlans] = useState<SystemSettings[]>([]);
    const [newPlan, setNewPlan] = useState({
        currency: 'GHS',
        amount_per_sms: 0,
        workmanship_fee: 0,
        sender_id_price: 0,
        is_active: false,
    });
    const [showCreatePlanForm, setShowCreatePlanForm] = useState(false);
    const [creatingPlan, setCreatingPlan] = useState(false);
    const [activatingPlanId, setActivatingPlanId] = useState<number | null>(null);
    const [auditPagination, setAuditPagination] = useState({
        page: 1,
        pageSize: 25,
        totalPages: 1,
        total: 0,
        hasNext: false,
        hasPrev: false,
    });
    const [auditLoading, setAuditLoading] = useState(false);
    const [report, setReport] = useState<RevenueReport | null>(null);
    const [activeSection, setActiveSection] = useState<AdminSection>('revenue');
    const [showExportOptions, setShowExportOptions] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedExportFields, setSelectedExportFields] = useState<string[]>([
        'campaign_name',
        'status',
        'total_sent',
        'total_failed',
        'estimated_revenue',
        'actual_revenue',
        'currency',
    ]);
    const [selectedExportStatuses, setSelectedExportStatuses] = useState<CampaignStatus[]>([]);
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const today = new Date().toISOString().slice(0, 10);
                setAuditStartDate(today);
                setAuditEndDate(today);

                const [settingsData, pricingPlansData, auditData, reportData, usersData] = await Promise.all([
                    adminApi.getSettings(),
                    adminApi.listPricingPlans(),
                    adminApi.getAuditLogs({ page_size: 25, start_date: today, end_date: today }),
                    adminApi.getRevenueReport(),
                    adminApi.listUsers(),
                ]);

                setSettings(settingsData);
                setPricingPlans(pricingPlansData);
                setUsers(usersData);
                setAuditLogs(auditData.items);
                setAuditPagination({
                    page: auditData.page,
                    pageSize: auditData.page_size,
                    totalPages: auditData.total_pages,
                    total: auditData.total,
                    hasNext: auditData.has_next,
                    hasPrev: auditData.has_prev,
                });
                setReport(reportData);
            } catch {
                showToast('Failed to load admin data', 'error');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [showToast]);

    const createPricingPlan = async (event: React.FormEvent) => {
        event.preventDefault();
        if (creatingPlan) {
            return;
        }

        try {
            setCreatingPlan(true);
            const newPricingPlan = await adminApi.createPricingPlan(newPlan);
            setPricingPlans((prev) => [newPricingPlan, ...prev]);
            setNewPlan({
                currency: newPlan.currency,
                amount_per_sms: 0,
                workmanship_fee: 0,
                sender_id_price: 0,
                is_active: false,
            });
            setShowCreatePlanForm(false);
            showToast('Pricing plan created', 'success');
        } catch {
            showToast('Failed to create pricing plan', 'error');
        } finally {
            setCreatingPlan(false);
        }
    };

    const activatePricingPlan = async (planId: number) => {
        try {
            setActivatingPlanId(planId);
            const plan = await adminApi.activatePricingPlan(planId);
            setPricingPlans((prev) =>
                prev.map((item) => ({
                    ...item,
                    is_active: item.id === plan.id,
                }))
            );
            setSettings(plan);
            showToast('Pricing plan activated', 'success');
        } catch {
            showToast('Failed to activate pricing plan', 'error');
        } finally {
            setActivatingPlanId(null);
        }
    };

    const createStaffUser = async (event: React.FormEvent) => {
        event.preventDefault();
        if (creatingUser) {
            return;
        }

        try {
            setCreatingUser(true);
            const createdUser = await adminApi.createUser({
                email: newUser.email.trim(),
                full_name: newUser.full_name.trim(),
                password: newUser.password,
                role: newUser.role,
            });
            setUsers((prev) => [createdUser, ...prev]);
            setNewUser({
                email: '',
                full_name: '',
                password: '',
                role: 'staff',
            });
            setShowCreateUserForm(false);
            showToast('Staff user created', 'success');
        } catch {
            showToast('Failed to create staff user', 'error');
        } finally {
            setCreatingUser(false);
        }
    };

    const updateStaffUser = async (userId: number, data: { role?: UserRole; is_active?: boolean }) => {
        try {
            setUpdatingUserId(userId);
            const updatedUser = await adminApi.updateUser(userId, data);
            setUsers((prev) => prev.map((user) => user.id === updatedUser.id ? updatedUser : user));
            showToast('Staff user updated', 'success');
        } catch {
            showToast('Failed to update staff user', 'error');
        } finally {
            setUpdatingUserId(null);
        }
    };

    const latestAudit = auditLogs[0];
    const currency = report?.totals.currency || settings?.currency || 'GHS';
    const deliveryRate = useMemo(() => {
        const rows = report?.rows || [];
        const sent = rows.reduce((total, row) => total + (row.total_sent || 0), 0);
        const failed = rows.reduce((total, row) => total + (row.total_failed || 0), 0);
        const completedAttempts = sent + failed;
        if (!completedAttempts) return 0;
        return Math.round((sent / completedAttempts) * 100);
    }, [report]);
    const totalFailed = report?.rows.reduce((total, row) => total + (row.total_failed || 0), 0) || 0;

    const loadAuditLogs = async (options?: {
        page?: number;
        userEmail?: string;
        startDate?: string;
        endDate?: string;
    }) => {
        setAuditLoading(true);
        try {
            const response = await adminApi.getAuditLogs({
                page: options?.page ?? auditPagination.page,
                page_size: auditPagination.pageSize,
                user_email: options?.userEmail || (auditUserFilter.trim() || undefined),
                start_date: options?.startDate || auditStartDate || undefined,
                end_date: options?.endDate || auditEndDate || undefined,
            });

            setAuditLogs(response.items);
            setAuditPagination({
                page: response.page,
                pageSize: response.page_size,
                totalPages: response.total_pages,
                total: response.total,
                hasNext: response.has_next,
                hasPrev: response.has_prev,
            });
        } catch {
            showToast('Failed to fetch audit logs', 'error');
        } finally {
            setAuditLoading(false);
        }
    };

    const handleAuditSearch = async (event: React.FormEvent) => {
        event.preventDefault();
        await loadAuditLogs({
            page: 1,
            userEmail: auditUserFilter.trim() || undefined,
            startDate: auditStartDate || undefined,
            endDate: auditEndDate || undefined,
        });
    };

    const handleAuditReset = async () => {
        const today = new Date().toISOString().slice(0, 10);
        setAuditUserFilter('');
        setAuditStartDate(today);
        setAuditEndDate(today);
        await loadAuditLogs({
            page: 1,
            userEmail: undefined,
            startDate: today,
            endDate: today,
        });
    };

    const handleAuditPageChange = async (newPage: number) => {
        if (newPage < 1 || newPage > auditPagination.totalPages) return;
        await loadAuditLogs({ page: newPage });
    };

    const toggleExportField = (field: string) => {
        setSelectedExportFields((current) =>
            current.includes(field)
                ? current.filter((item) => item !== field)
                : [...current, field]
        );
    };

    const toggleExportStatus = (status: CampaignStatus) => {
        setSelectedExportStatuses((current) =>
            current.includes(status)
                ? current.filter((item) => item !== status)
                : [...current, status]
        );
    };

    const exportRevenue = async () => {
        if (!selectedExportFields.length) {
            showToast('Select at least one export column', 'error');
            return;
        }

        try {
            setExporting(true);
            await adminApi.exportRevenueReport({
                start_date: exportStartDate || undefined,
                end_date: exportEndDate || undefined,
                fields: selectedExportFields,
                status_filter: selectedExportStatuses,
            });
            showToast('Revenue export downloaded', 'success');
        } catch {
            showToast('Failed to export revenue report', 'error');
        } finally {
            setExporting(false);
        }
    };

    if (loading || !settings) {
        return <LoadingSpinner fullScreen text="Loading admin console..." />;
    }

    const activePlan = pricingPlans.find((plan) => plan.is_active) || settings;
    const activeUsers = users.filter((user) => user.is_active).length;
    const adminUsers = users.filter((user) => user.role === 'admin').length;

    const sections: Array<{ id: AdminSection; label: string; count?: number }> = [
        { id: 'revenue', label: 'Revenue', count: report?.totals.campaigns || 0 },
        { id: 'pricing', label: 'Pricing' },
        { id: 'staff', label: 'Staff', count: users.length },
        { id: 'audit', label: 'Audit', count: auditPagination.total },
    ];

    return (
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-5 rounded-lg border border-gray-200 bg-white px-5 py-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Administration</p>
                    <h1 className="mt-1 text-3xl font-bold tracking-tight text-gray-950">Admin Console</h1>
                    <p className="mt-2 max-w-2xl text-sm text-gray-600">
                        Manage commercial controls, revenue performance, and operational audit activity.
                    </p>
                </div>
                <div className="grid grid-cols-2 rounded-lg border border-gray-200 bg-gray-50 p-1 shadow-inner sm:min-w-[28rem] sm:grid-cols-4">
                    {sections.map((section) => (
                        <button
                            key={section.id}
                            type="button"
                            onClick={() => setActiveSection(section.id)}
                            className={clsx(
                                'rounded-md px-4 py-2 text-sm font-medium transition-colors',
                                activeSection === section.id
                                    ? 'bg-gray-950 text-white shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-950'
                            )}
                        >
                            {section.label}
                            {typeof section.count === 'number' && (
                                <span className={clsx(
                                    'ml-2 rounded-full px-1.5 py-0.5 text-xs',
                                    activeSection === section.id ? 'bg-white/15 text-white' : 'bg-gray-100 text-gray-600'
                                )}>
                                    {section.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <StatCard
                    label="Actual Revenue"
                    value={money(report?.totals.actual_revenue || 0, currency)}
                    detail={`${report?.totals.campaigns || 0} campaigns tracked`}
                    helper="Live"
                    tone="green"
                />
                <StatCard
                    label="Estimated Revenue"
                    value={money(report?.totals.estimated_revenue || 0, currency)}
                    detail={`${formatNumber(report?.totals.total_sent || 0)} messages sent`}
                    helper="Forecast"
                />
                <StatCard
                    label="Delivery Rate"
                    value={`${deliveryRate}%`}
                    detail={`${formatNumber(totalFailed)} failed attempts`}
                    helper="Quality"
                    tone="blue"
                />
                <StatCard
                    label="Latest Audit"
                    value={prettyAction(latestAudit?.action)}
                    detail={latestAudit ? `${formatRelativeTime(latestAudit.created_at)} by ${latestAudit.user_email || 'System'}` : 'No recent activity'}
                    helper={latestAudit?.status_code ? String(latestAudit.status_code) : undefined}
                />
            </section>

            <section className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                {activeSection === 'revenue' && (
                    <div>
                        <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-950">Revenue Report</h2>
                                <p className="mt-1 text-sm text-gray-500">
                                    {formatNumber(report?.rows.length || 0)} campaigns with billing and realized revenue.
                                </p>
                            </div>
                            <Button variant="outline" onClick={() => setShowExportOptions((current) => !current)}>
                                {showExportOptions ? 'Hide Export' : 'Export Options'}
                            </Button>
                        </div>
                        {showExportOptions && (
                            <div className="border-b border-gray-200 bg-gray-50 px-5 py-5">
                                <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.4fr_1fr_220px]">
                                    <div>
                                        <div className="flex items-center justify-between gap-3">
                                            <h3 className="text-sm font-semibold text-gray-950">Columns</h3>
                                            <button
                                                type="button"
                                                className="text-sm font-medium text-blue-700 hover:text-blue-800"
                                                onClick={() => setSelectedExportFields(exportFields.map((field) => field.key))}
                                            >
                                                Select all
                                            </button>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                                            {exportFields.map((field) => (
                                                <label key={field.key} className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={selectedExportFields.includes(field.key)}
                                                        onChange={() => toggleExportField(field.key)}
                                                    />
                                                    {field.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-950">Campaign Status</h3>
                                        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1">
                                            {exportStatuses.map((status) => (
                                                <label key={status} className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={selectedExportStatuses.includes(status)}
                                                        onChange={() => toggleExportStatus(status)}
                                                    />
                                                    <span className="capitalize">{status.replace('_', ' ')}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <p className="mt-2 text-xs text-gray-500">Leave all unchecked to include every status.</p>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-950">Date Range</h3>
                                        <div className="mt-3 space-y-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                                                <input
                                                    type="date"
                                                    value={exportStartDate}
                                                    onChange={(event) => setExportStartDate(event.target.value)}
                                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                                                <input
                                                    type="date"
                                                    value={exportEndDate}
                                                    onChange={(event) => setExportEndDate(event.target.value)}
                                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                            <Button onClick={exportRevenue} loading={exporting} fullWidth>
                                                Download CSV
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Campaign</th>
                                        <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Sent</th>
                                        <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Failed</th>
                                        <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Estimated</th>
                                        <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actual</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {(report?.rows || []).length === 0 && (
                                        <EmptyState
                                            colSpan={6}
                                            title="No revenue yet"
                                            detail="Campaigns will appear here once billing activity is recorded."
                                        />
                                    )}
                                    {(report?.rows || []).slice(0, 12).map((row) => (
                                        <tr key={row.campaign_id} className="hover:bg-gray-50">
                                            <td className="px-5 py-4 text-gray-950">
                                                <div className="max-w-xs truncate font-medium">{row.campaign_name}</div>
                                                <div className="mt-0.5 text-xs text-gray-500">{formatDate(row.created_at)}</div>
                                            </td>
                                            <td className="px-5 py-4 text-right tabular-nums text-gray-600">{formatNumber(row.total_sent)}</td>
                                            <td className="px-5 py-4 text-right tabular-nums text-gray-600">{formatNumber(row.total_failed)}</td>
                                            <td className="px-5 py-4 text-right tabular-nums text-gray-900">{money(row.estimated_revenue, row.currency)}</td>
                                            <td className="px-5 py-4 text-right font-semibold tabular-nums text-gray-950">{money(row.actual_revenue, row.currency)}</td>
                                            <td className="px-5 py-4"><span className={statusClass(row.status)}>{row.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeSection === 'pricing' && (
                    <div>
                        <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-950">Pricing Settings</h2>
                                <p className="mt-1 text-sm text-gray-500">
                                    Active plan: {activePlan.currency}. {formatNumber(pricingPlans.length)} saved pricing plans.
                                </p>
                            </div>
                            <Button
                                variant={showCreatePlanForm ? 'outline' : 'primary'}
                                onClick={() => setShowCreatePlanForm((current) => !current)}
                                disabled={creatingPlan}
                            >
                                {showCreatePlanForm ? 'Cancel Plan' : 'New Pricing Plan'}
                            </Button>
                        </div>

                        <div className="grid gap-6 border-b border-gray-200 bg-gray-50/50 p-5 lg:grid-cols-[1.35fr_minmax(320px,420px)]">
                            <div className="p-5">
                                <div className="flex items-center justify-between gap-3">
                                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Active Billing Model</h3>
                                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">Active</span>
                                </div>
                                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                        <p className="text-sm text-gray-500">Per SMS</p>
                                        <p className="mt-2 text-2xl font-bold tracking-tight text-gray-950">{money(activePlan.amount_per_sms, activePlan.currency)}</p>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                        <p className="text-sm text-gray-500">Workmanship</p>
                                        <p className="mt-2 text-2xl font-bold tracking-tight text-gray-950">{money(activePlan.workmanship_fee, activePlan.currency)}</p>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                        <p className="text-sm text-gray-500">Sender ID</p>
                                        <p className="mt-2 text-2xl font-bold tracking-tight text-gray-950">{money(activePlan.sender_id_price, activePlan.currency)}</p>
                                    </div>
                                </div>
                                <dl className="mt-6 divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                                    <div className="flex items-center justify-between px-4 py-3 text-sm">
                                        <dt className="text-gray-500">Last updated</dt>
                                        <dd className="font-medium text-gray-900">{formatDate(activePlan.updated_at)}</dd>
                                    </div>
                                    <div className="flex items-center justify-between px-4 py-3 text-sm">
                                        <dt className="text-gray-500">Currency code</dt>
                                        <dd className="font-medium text-gray-900">{activePlan.currency}</dd>
                                    </div>
                                    <div className="flex items-center justify-between px-4 py-3 text-sm">
                                        <dt className="text-gray-500">Plan status</dt>
                                        <dd className="font-medium text-gray-900">Active</dd>
                                    </div>
                                </dl>
                            </div>

                            {showCreatePlanForm && (
                                <form onSubmit={createPricingPlan} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                                    <div>
                                        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Create Pricing Plan</h3>
                                        <p className="mt-1 text-sm text-gray-500">Save a draft plan, then activate it from the list.</p>
                                    </div>
                                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                            <input
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                value={newPlan.currency}
                                                onChange={(e) => setNewPlan({ ...newPlan, currency: e.target.value.toUpperCase() })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Amount per SMS</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.0001"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                value={newPlan.amount_per_sms}
                                                onChange={(e) => setNewPlan({ ...newPlan, amount_per_sms: Number(e.target.value) })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Workmanship</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                value={newPlan.workmanship_fee}
                                                onChange={(e) => setNewPlan({ ...newPlan, workmanship_fee: Number(e.target.value) })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Sender ID price</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                value={newPlan.sender_id_price}
                                                onChange={(e) => setNewPlan({ ...newPlan, sender_id_price: Number(e.target.value) })}
                                            />
                                        </div>
                                        <Button type="submit" fullWidth loading={creatingPlan}>
                                            Create Plan
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </div>

                        <div className="overflow-hidden bg-white">
                            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900">Pricing Plans</h3>
                                    <p className="mt-1 text-xs text-gray-500">Compare rates and switch the active billing model.</p>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Plan</th>
                                            <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Per SMS</th>
                                            <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Workmanship</th>
                                            <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Sender ID</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                                            <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {pricingPlans.length === 0 && (
                                            <EmptyState
                                                colSpan={6}
                                                title="No pricing plans"
                                                detail="Create your first plan to start managing billing changes."
                                            />
                                        )}
                                        {pricingPlans.map((plan) => (
                                            <tr key={plan.id} className="hover:bg-gray-50">
                                                <td className="px-5 py-4 text-gray-950">
                                                    <div className="font-medium">{plan.currency} plan</div>
                                                    <div className="mt-0.5 text-xs text-gray-500">Created {formatDate(plan.created_at)}</div>
                                                </td>
                                                <td className="px-5 py-4 text-right tabular-nums text-gray-700">{money(plan.amount_per_sms, plan.currency)}</td>
                                                <td className="px-5 py-4 text-right tabular-nums text-gray-700">{money(plan.workmanship_fee, plan.currency)}</td>
                                                <td className="px-5 py-4 text-right tabular-nums text-gray-700">{money(plan.sender_id_price, plan.currency)}</td>
                                                <td className="px-5 py-4">
                                                    <span className={clsx(
                                                        'inline-flex rounded-full px-2.5 py-1 text-xs font-semibold',
                                                        plan.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                                                    )}>
                                                        {plan.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-4 text-right">
                                                    {!plan.is_active && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            loading={activatingPlanId === plan.id}
                                                            onClick={() => activatePricingPlan(plan.id)}
                                                        >
                                                            Activate
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeSection === 'staff' && (
                    <div>
                        <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-950">Staff Management</h2>
                                <p className="mt-1 text-sm text-gray-500">
                                    {formatNumber(activeUsers)} active users, {formatNumber(adminUsers)} administrators.
                                </p>
                            </div>
                            <Button
                                variant={showCreateUserForm ? 'outline' : 'primary'}
                                onClick={() => setShowCreateUserForm((current) => !current)}
                                disabled={creatingUser}
                            >
                                {showCreateUserForm ? 'Cancel User' : 'New Staff'}
                            </Button>
                        </div>

                        {showCreateUserForm && (
                            <form onSubmit={createStaffUser} className="grid gap-4 border-b border-gray-200 bg-gray-50 px-5 py-5 lg:grid-cols-[1fr_1fr_220px_180px_auto]">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Full name</label>
                                    <input
                                        type="text"
                                        value={newUser.full_name}
                                        onChange={(event) => setNewUser({ ...newUser, full_name: event.target.value })}
                                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Staff member name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                                    <input
                                        type="email"
                                        value={newUser.email}
                                        onChange={(event) => setNewUser({ ...newUser, email: event.target.value })}
                                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="name@company.com"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Temporary password</label>
                                    <input
                                        type="password"
                                        value={newUser.password}
                                        onChange={(event) => setNewUser({ ...newUser, password: event.target.value })}
                                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        minLength={10}
                                        placeholder="10+ characters"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
                                    <select
                                        value={newUser.role}
                                        onChange={(event) => setNewUser({ ...newUser, role: event.target.value as UserRole })}
                                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {roleOptions.map((role) => (
                                            <option key={role} value={role}>{role}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-end">
                                    <Button type="submit" loading={creatingUser}>
                                        Create Staff
                                    </Button>
                                </div>
                            </form>
                        )}

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">User</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Role</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Last Login</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                                        <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {users.length === 0 && (
                                        <EmptyState
                                            colSpan={5}
                                            title="No staff users"
                                            detail="Create a staff member to delegate operational access."
                                        />
                                    )}
                                    {users.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50">
                                            <td className="px-5 py-4 text-gray-950">
                                                <div className="max-w-64 truncate font-medium">{user.full_name}</div>
                                                <div className="mt-0.5 max-w-64 truncate text-xs text-gray-500">{user.email}</div>
                                            </td>
                                            <td className="px-5 py-4">
                                                <select
                                                    value={user.role}
                                                    onChange={(event) => updateStaffUser(user.id, { role: event.target.value as UserRole })}
                                                    disabled={updatingUserId === user.id}
                                                    className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                                >
                                                    {roleOptions.map((role) => (
                                                        <option key={role} value={role}>{role}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-5 py-4 text-gray-600 whitespace-nowrap">
                                                {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className={clsx(
                                                    'inline-flex rounded-full px-2.5 py-1 text-xs font-semibold',
                                                    user.is_active ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-gray-100 text-gray-600'
                                                )}>
                                                    {user.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    loading={updatingUserId === user.id}
                                                    onClick={() => updateStaffUser(user.id, { is_active: !user.is_active })}
                                                >
                                                    {user.is_active ? 'Deactivate' : 'Activate'}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeSection === 'audit' && (
                    <div>
                        <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-950">Audit Logs</h2>
                                <p className="mt-1 text-sm text-gray-500">
                                    {formatNumber(auditPagination.total)} recent account, settings, and campaign events.
                                </p>
                            </div>
                            {auditLoading && <span className="text-sm font-medium text-blue-700">Refreshing...</span>}
                        </div>
                        <form onSubmit={handleAuditSearch} className="grid gap-4 border-b border-gray-200 bg-gray-50 px-5 py-5 sm:grid-cols-6">
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Search by user</label>
                                <input
                                    type="text"
                                    value={auditUserFilter}
                                    onChange={(event) => setAuditUserFilter(event.target.value)}
                                    placeholder="Filter by user email"
                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="sm:col-span-2 lg:col-span-1">
                                <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                                <input
                                    type="date"
                                    value={auditStartDate}
                                    onChange={(event) => setAuditStartDate(event.target.value)}
                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="sm:col-span-2 lg:col-span-1">
                                <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                                <input
                                    type="date"
                                    value={auditEndDate}
                                    onChange={(event) => setAuditEndDate(event.target.value)}
                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="flex items-end gap-2 sm:col-span-6 lg:col-span-2">
                                <Button type="submit" loading={auditLoading}>
                                    Apply filters
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleAuditReset}
                                    disabled={auditLoading}
                                >
                                    Reset
                                </Button>
                            </div>
                        </form>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Time</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">User</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Action</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Resource</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">IP</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {auditLogs.length === 0 && (
                                        <EmptyState
                                            colSpan={6}
                                            title="No audit logs found"
                                            detail="Adjust the filters or reset to today's activity."
                                        />
                                    )}
                                    {auditLogs.map((log) => (
                                        <tr key={log.id} className="hover:bg-gray-50">
                                            <td className="px-5 py-4 text-gray-600 whitespace-nowrap">{formatDate(log.created_at)}</td>
                                            <td className="px-5 py-4 text-gray-950">
                                                <span className="block max-w-56 truncate">{log.user_email || 'System'}</span>
                                            </td>
                                            <td className="px-5 py-4 font-medium text-gray-950">
                                                <span className="block max-w-64 truncate" title={log.action}>{prettyAction(log.action)}</span>
                                            </td>
                                            <td className="px-5 py-4 text-gray-600">
                                                <span className="block max-w-44 truncate">{log.resource_type || '-'} {log.resource_id || ''}</span>
                                            </td>
                                            <td className="px-5 py-4 font-mono text-xs text-gray-600">{log.ip_address || '-'}</td>
                                            <td className="px-5 py-4">
                                                <span className={statusCodeClass(log.status_code)}>{log.status_code || '-'}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex flex-col gap-3 justify-between px-5 py-4 sm:flex-row sm:items-center">
                            <p className="text-sm text-gray-500">
                                Showing {auditLogs.length} of {auditPagination.total} audit entries
                            </p>
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleAuditPageChange(auditPagination.page - 1)}
                                    disabled={!auditPagination.hasPrev || auditLoading}
                                >
                                    Previous
                                </Button>
                                <span className="text-sm text-gray-600">
                                    Page {auditPagination.page} of {auditPagination.totalPages}
                                </span>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleAuditPageChange(auditPagination.page + 1)}
                                    disabled={!auditPagination.hasNext || auditLoading}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </main>
    );
};

export default AdminPage;
